import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useAbsencePlannerStateContext from 'pages/AbsencePlanner/contexts/AbsencePlannerStateContext'
import { SIDE_MENU_OPTIONS, TASK_LIST_PAGES } from 'redesign/components/templates/SideMenu/constants'
import type { MenuOptions } from 'redesign/components/templates/SideMenu/types'
import { getSideMenuConfiguration } from 'redesign/components/templates/SideMenu/utils'
import { useAppStatesContext } from 'services/AppStates'
import useCms from '../../../hooks/useCms'
import { useFiltersStorageContext } from '../FiltersStorage/useFiltersStorageContext'
import { SIDE_MENU_CURRENT_PAGE } from './constants'
import SideMenuContext from './context'
import type { SideMenuProviderProps } from './types'

const SideMenuProvider: React.FC<SideMenuProviderProps> = ({ children, parentPage, persons }) => {
  const { selectedOwnerId } = useAbsencePlannerStateContext()
  const { get, clear } = useAppStatesContext()
  const refSideMenu = get(SIDE_MENU_CURRENT_PAGE)
  const [page, setPage] = useState<MenuOptions | undefined>(refSideMenu ?? SIDE_MENU_OPTIONS.mainMenu)
  const [open, setOpen] = useState(Boolean(refSideMenu))
  const [taskCount, setTaskCount] = useState<number | undefined>()
  const isAbsenceManager = useMemo(
    () =>
      (persons ?? [])
        .filter((person) => `${person.ownerId}` === `${selectedOwnerId}`)
        .some((person) => person.permissions.workflowAbsences),
    [persons, selectedOwnerId],
  )
  const isShiftUpdateManager = useMemo(
    () => (persons ?? []).some((person) => person.permissions.workflowShiftUpdate),
    [persons],
  )
  const { cmsAccess } = useCms()
  const { saveFilters } = useFiltersStorageContext()
  const pages = getSideMenuConfiguration({
    parentPage,
    isAbsenceManager,
    isShiftUpdateManager,
    hasCmsAccess: cmsAccess,
  })

  const onPageChange = useCallback(
    (name?: MenuOptions) => {
      if (page && TASK_LIST_PAGES.includes(page)) {
        saveFilters()
      }

      setPage(name)
    },
    [page, saveFilters],
  )

  useEffect(() => {
    clear(SIDE_MENU_CURRENT_PAGE)
    // clear makes too many re-renders and affects performance
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SideMenuContext.Provider
      value={{
        page,
        setPage: onPageChange,
        pages,
        open,
        setOpen,
        parentPage,
        isAbsenceManager,
        isShiftUpdateManager,
        taskCount,
        setTaskCount,
      }}
    >
      {children}
    </SideMenuContext.Provider>
  )
}

export default SideMenuProvider
